/**
 * Калькулятор прибыли — прибыль с одной проданной штуки, как в «Калькуляторе прибыли» кабинета WB
 * (просьба заказчика 21.10.2026: «перенести калькулятор в приложение, по каждой модели вводить
 * себестоимость и расходы, маржа за штучку сохранялась, и по ней всё считалось»).
 *
 * Один расчёт на экране (мини-апп) и на сервере: файл без зависимостей, мини-апп берёт его
 * точкой входа @wb/contracts/profit — иначе цифры экрана и отчётов разошлись бы.
 *
 * Как считает WB (сверено с отчётами о реализации PixAi, HK89 Pro, 18.10.2026):
 *   к перечислению = цена со скидкой продавца × (1 − КВВ) − эквайринг; СПП WB даёт за свой счёт
 *   и на выплату продавцу не влияет. Логистика, хранение и приёмка удерживаются отдельно.
 * Налоги — по режиму; НДС (с 2026 у УСН при доходе больше 20 млн ₽: 5 %, 7 % или 22 %) — из цены.
 */
export const TAX_REGIMES = {
    // Решение заказчика 22.10.2026: налог на доход в калькуляторе не считать — «с чистой прибыли, что снимем,
    // налог уже сами решим»; считается только НДС с вычетами. Режим калькулятора по умолчанию.
    none: { title: 'Не учитывать — налог на доход считаю сам', rate: 0, min: 0, max: 0, kind: 'income', min_tax_pct: 0 },
    usn_income: { title: 'УСН «доходы»', rate: 6, min: 1, max: 6, kind: 'income', min_tax_pct: 0 },
    usn_income_expense: { title: 'УСН «доходы минус расходы»', rate: 15, min: 5, max: 15, kind: 'profit', min_tax_pct: 1 },
    ausn_income: { title: 'АУСН «доходы»', rate: 8, min: 8, max: 8, kind: 'income', min_tax_pct: 0 },
    ausn_income_expense: { title: 'АУСН «доходы минус расходы»', rate: 20, min: 20, max: 20, kind: 'profit', min_tax_pct: 3 },
    npd: { title: 'НПД (самозанятый)', rate: 4, min: 4, max: 6, kind: 'income', min_tax_pct: 0 },
    osno_ip: { title: 'ОСНО, ИП (НДФЛ)', rate: 13, min: 13, max: 22, kind: 'profit', min_tax_pct: 0 },
    osno_org: { title: 'ОСНО, ООО (налог на прибыль)', rate: 25, min: 25, max: 25, kind: 'profit', min_tax_pct: 0 },
    patent: { title: 'Патент (стоимость — в прочих расходах)', rate: 0, min: 0, max: 0, kind: 'income', min_tax_pct: 0 },
};
export const TAX_BASES = {
    sale: 'сумма продажи по отчёту WB — что заплатил покупатель',
    price: 'цена со скидкой продавца',
    to_transfer: 'сумма «к перечислению» от WB',
};
export const VAT_RATES = [0, 5, 7, 10, 22];
const r2 = (x) => Math.round(x * 100) / 100;
const n = (x) => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
export function computeProfit(i, t) {
    const price = n(i.price_before_discount) * (1 - n(i.seller_discount_pct) / 100);
    const commission = price * n(i.commission_pct) / 100;
    const acquiring = price * n(i.acquiring_pct) / 100;
    const logistics = n(i.logistics_rub), storage = n(i.storage_rub), acceptance = n(i.acceptance_rub);
    const wb_total = commission + acquiring + logistics + storage + acceptance;
    const to_transfer = price - wb_total;
    const cogs_total = n(i.cogs_rub) + n(i.packaging_rub) + n(i.inbound_rub);
    const ads = price * n(i.ads_pct) / 100;
    const other = n(i.other_rub);
    const reg = TAX_REGIMES[t.regime] ?? TAX_REGIMES.usn_income;
    // Доход для налога и НДС: что заплатил покупатель (после СПП), цена со скидкой или к перечислению.
    const gross = t.base === 'price' ? price : t.base === 'to_transfer' ? to_transfer : price * (1 - n(i.spp_pct) / 100);
    // НДС к уплате = НДС с продажи − входящий НДС (схема заказчика 22.10.2026). НДС — «в том числе» в цене:
    // доля 22/122 = 18,03 %, а не 1/6 (это доля при прежних 20 % — заказчик подтвердил 22 %). Вычеты — только
    // при общей ставке (10 и 22 %): на УСН со ставками 5 % и 7 % входящий НДС к вычету не принимается.
    // Ответы заказчика 21.10.2026: НДС в себестоимости — 22/122 от неё (ввозной НДС отдельно не вводится);
    // в услугах WB — без эквайринга (по закону НДС не облагается); реклама облагается — её НДС тоже к вычету.
    const vatPct = n(t.vat_pct);
    const share = vatPct > 0 ? vatPct / (100 + vatPct) : 0;
    const vat_sale = gross * share;
    const deduct = vatPct >= 10;
    const vat_in_cogs = deduct ? (i.cogs_vat_rub != null && Number.isFinite(i.cogs_vat_rub) ? i.cogs_vat_rub : cogs_total * share) : 0;
    const vat_in_wb = deduct ? (commission + logistics + storage + acceptance) * share : 0;
    const vat_in_ads = deduct ? ads * share : 0;
    const vat = vat_sale - vat_in_cogs - vat_in_wb - vat_in_ads;
    const income = gross - vat_sale;
    let tax_base, tax;
    if (reg.kind === 'income') {
        tax_base = income;
        tax = income * n(t.rate_pct) / 100;
    }
    else {
        // «Доходы минус расходы»: выплата WB минус ваши расходы и НДС; не меньше минимального налога от дохода.
        tax_base = Math.max(0, to_transfer - cogs_total - ads - other - vat);
        tax = Math.max(tax_base * n(t.rate_pct) / 100, income * reg.min_tax_pct / 100);
    }
    const profit = to_transfer - cogs_total - ads - other - vat - tax;
    return {
        price: r2(price), commission: r2(commission), acquiring: r2(acquiring), logistics: r2(logistics), storage: r2(storage), acceptance: r2(acceptance),
        wb_total: r2(wb_total), to_transfer: r2(to_transfer), cogs_total: r2(cogs_total), ads: r2(ads), other: r2(other),
        tax_base: r2(tax_base), vat_sale: r2(vat_sale), vat_in_cogs: r2(vat_in_cogs), vat_in_wb: r2(vat_in_wb), vat_in_ads: r2(vat_in_ads), vat: r2(vat), tax: r2(tax), profit: r2(profit),
        margin_pct: price > 0 ? r2(100 * profit / price) : null,
        roi_pct: cogs_total > 0 ? r2(100 * profit / cogs_total) : null,
    };
}
/** Проверка ввода: что не так — словами; пусто — всё верно. */
export function checkProfitInputs(i, t) {
    const out = [];
    const pct = (v, name, max = 100) => { if (!(v >= 0 && v <= max))
        out.push(`${name} — от 0 до ${max} %`); };
    if (!(i.price_before_discount > 0))
        out.push('Цена до скидки — больше нуля');
    pct(i.seller_discount_pct, 'Скидка продавца', 99);
    pct(i.commission_pct, 'Комиссия WB');
    pct(i.acquiring_pct, 'Эквайринг', 20);
    pct(i.ads_pct, 'Реклама');
    pct(i.spp_pct, 'СПП', 99);
    for (const [v, name] of [[i.logistics_rub, 'Логистика'], [i.storage_rub, 'Хранение'], [i.acceptance_rub, 'Приёмка'], [i.cogs_rub, 'Себестоимость'],
        [i.packaging_rub, 'Упаковка'], [i.inbound_rub, 'Доставка до склада'], [i.other_rub, 'Прочие расходы']]) {
        if (!(v >= 0))
            out.push(`${name} — не меньше нуля`);
    }
    const reg = TAX_REGIMES[t.regime];
    if (reg === undefined)
        out.push('Выберите систему налогообложения');
    else if (!(t.rate_pct >= reg.min && t.rate_pct <= reg.max))
        out.push(reg.min === reg.max ? `Ставка для «${reg.title}» — ${reg.min} %` : `Ставка для «${reg.title}» — от ${reg.min} до ${reg.max} %`);
    if (!VAT_RATES.includes(t.vat_pct))
        out.push('НДС — 0, 5, 7, 10 или 22 %');
    if (!(t.base in TAX_BASES))
        out.push('Выберите базу налога');
    if (i.cogs_vat_rub != null && !(i.cogs_vat_rub >= 0))
        out.push('НДС в себестоимости — не меньше нуля');
    return out;
}
//# sourceMappingURL=profit.js.map